import { parseError } from "@/lib/parse-error";

import type {
  EnrollmentDetail,
  EnrollmentExecutionDetail,
  EnrollmentFetchParams,
  OutreachSequenceCreate,
  OutreachSequenceDetail,
  OutreachSequenceFetchParams,
  OutreachSequenceListItem,
  OutreachSequenceUpdate,
} from "@/types/outreach-sequence";
import type { Page } from "@/types/pagination";

import { requestData } from "@/api/api-config";

// ─── Outreach Sequences ──────────────────────────────────────────────────────

export async function fetchOutreachSequences(
  params: OutreachSequenceFetchParams,
): Promise<Page<OutreachSequenceListItem> | null> {
  return await requestData<Page<OutreachSequenceListItem>>(
    "comms/outreach-sequences",
    "get",
    { searchParams: params as Record<string, string> },
  );
}

export async function getOutreachSequence(
  sequenceId: string,
): Promise<OutreachSequenceDetail | null> {
  return await requestData<OutreachSequenceDetail>(
    `comms/outreach-sequences/${sequenceId}`,
  );
}

export async function createOutreachSequence(
  payload: OutreachSequenceCreate,
): Promise<OutreachSequenceDetail> {
  try {
    return (await requestData<OutreachSequenceDetail>(
      "comms/outreach-sequences",
      "post",
      { json: payload },
      false,
    ))!;
  } catch (error: any) {
    throw new Error(
      await parseError(error, "Failed to create outreach sequence"),
    );
  }
}

export async function updateOutreachSequence(
  sequenceId: string,
  payload: OutreachSequenceUpdate,
): Promise<OutreachSequenceDetail> {
  try {
    return (await requestData<OutreachSequenceDetail>(
      `comms/outreach-sequences/${sequenceId}`,
      "put",
      { json: payload },
      false,
    ))!;
  } catch (error: any) {
    throw new Error(
      await parseError(error, "Failed to update outreach sequence"),
    );
  }
}

export async function deleteOutreachSequence(
  sequenceId: string,
): Promise<boolean> {
  try {
    await requestData(
      `comms/outreach-sequences/${sequenceId}`,
      "delete",
      undefined,
      false,
    );
    return true;
  } catch (error: any) {
    throw new Error(
      await parseError(error, "Failed to delete outreach sequence"),
    );
  }
}

export async function activateOutreachSequence(
  sequenceId: string,
  sendImmediately: boolean = false,
): Promise<OutreachSequenceDetail> {
  try {
    return (await requestData<OutreachSequenceDetail>(
      `comms/outreach-sequences/${sequenceId}/activate`,
      "post",
      // `send_immediately` is a query param (not a camelCased body field).
      sendImmediately
        ? { searchParams: { send_immediately: "true" } }
        : undefined,
      false,
    ))!;
  } catch (error: any) {
    throw new Error(
      await parseError(error, "Failed to activate outreach sequence"),
    );
  }
}

export async function pauseOutreachSequence(
  sequenceId: string,
): Promise<OutreachSequenceDetail> {
  try {
    return (await requestData<OutreachSequenceDetail>(
      `comms/outreach-sequences/${sequenceId}/pause`,
      "post",
      undefined,
      false,
    ))!;
  } catch (error: any) {
    throw new Error(
      await parseError(error, "Failed to pause outreach sequence"),
    );
  }
}

// ─── Enrollments ─────────────────────────────────────────────────────────────

export async function fetchEnrollments(
  sequenceId: string,
  params: EnrollmentFetchParams,
): Promise<Page<EnrollmentDetail> | null> {
  return await requestData<Page<EnrollmentDetail>>(
    `comms/outreach-sequences/${sequenceId}/enrollments`,
    "get",
    { searchParams: params as Record<string, string> },
  );
}

export async function fetchEnrollmentExecutions(
  sequenceId: string,
  enrollmentId: string,
): Promise<EnrollmentExecutionDetail[] | null> {
  return await requestData<EnrollmentExecutionDetail[]>(
    `comms/outreach-sequences/${sequenceId}/enrollments/${enrollmentId}/executions`,
  );
}
